// The seed held between reloads, so that a reload — a new build taken, a page refreshed by
// habit — does not cost the passcode again.
//
// The seed is in the page only while the app is unlocked (design, "What a device keeps"). A
// reload is a fresh page, so on its own it forgets. What is kept here is not the seed but the
// seed sealed under a key the browser made and will never hand out: an AES key generated
// non-extractable, kept in IndexedDB, which the browser will use on this page's behalf but
// never show. The sealed seed sits in sessionStorage, which lives exactly as long as the tab.
// The key alone opens nothing; the sealed seed alone is noise; and the two meet only in a page
// of this origin, which could read the seed from memory anyway. Close the tab and the sealed
// copy is gone. What is at rest between visits is what it always was: the vault.
//
// A tab on a phone can live for days, so a time is kept with it: the last moment the app was
// used. The seed is given back only within the limit the person chose, and the app locks
// itself when that has passed, whether or not the page was reloaded.

const DB = 'cb-hold';
const STORE = 'keys';
const KEY_ID = 'hold';
const HELD = 'cb.hold';

interface Held { iv: string; ct: string; at: number }

const b64 = (b: Uint8Array): string => btoa(String.fromCharCode(...b));
const unb64 = (s: string): Uint8Array<ArrayBuffer> => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
const bytes = (b: Uint8Array): Uint8Array<ArrayBuffer> => new Uint8Array(b);   // its own buffer, as WebCrypto wants

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => { req.result.createObjectStore(STORE); };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withKeys<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const req = fn(db.transaction(STORE, mode).objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

const readHeld = (): Held | null => {
  try { return JSON.parse(sessionStorage.getItem(HELD) ?? 'null') as Held | null; } catch { return null; }
};
const writeHeld = (h: Held | null): void => {
  try { if (h) sessionStorage.setItem(HELD, JSON.stringify(h)); else sessionStorage.removeItem(HELD); } catch { /* storage off: nothing held, nothing lost */ }
};

/** Hold the seed for this tab. Quiet on failure: the passcode is then asked as before. */
export async function holdSeed(seed: Uint8Array): Promise<void> {
  try {
    const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
    await withKeys('readwrite', (s) => s.put(key, KEY_ID));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, bytes(seed)));
    writeHeld({ iv: b64(iv), ct: b64(ct), at: Date.now() });
  } catch {
    writeHeld(null);
  }
}

/** The seed held for this tab, if there is one and it was used within `limitMs`. Anything
 *  else — nothing held, too long ago, the key gone, a copy that will not open — is dropped
 *  and answered null. */
export async function heldSeed(limitMs: number): Promise<Uint8Array | null> {
  const held = readHeld();
  if (!held) return null;
  if (Date.now() - held.at > limitMs) { await dropHeld(); return null; }
  try {
    const key = await withKeys<CryptoKey | undefined>('readonly', (s) => s.get(KEY_ID));
    if (!key) { writeHeld(null); return null; }
    return new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: unb64(held.iv) }, key, unb64(held.ct)));
  } catch {
    await dropHeld();
    return null;
  }
}

/** The app was used just now. */
export function touchHeld(): void {
  const held = readHeld();
  if (held) writeHeld({ ...held, at: Date.now() });
}

/** When the app was last used, as the hold saw it; null when nothing is held. */
export function heldSince(): number | null { return readHeld()?.at ?? null; }

/** Forget it: locking, leaving the device. The sealed copy goes first, since it is the part
 *  that is quick and certain; the key follows. */
export async function dropHeld(): Promise<void> {
  writeHeld(null);
  try { await withKeys('readwrite', (s) => s.delete(KEY_ID)); } catch { /* no db, or none to delete */ }
}
